/**
 * authGateway — единая точка входа авторизации.
 *
 * Зачем. Рублокс переезжает с user-сервиса v1 на identity v2, и переключение
 * нельзя делать «в один коммит»: если что-то пойдёт не так, разлогинит всех
 * живых пользователей сразу. Поэтому обе реализации живут рядом, а выбор
 * делает переменная окружения:
 *
 *     REACT_APP_IDENTITY=v2   → identity v2 (новый)
 *     не задана / =v1         → user-сервис v1 (текущий)
 *
 * Откат — смена одной переменной и пересборка, без правки кода.
 *
 * Контракт наружу одинаков для обеих версий:
 *   {token, userId, username, photo}
 * Это позволяет AuthContext не знать, какая версия активна.
 *
 * ── Что РАЗЛИЧАЕТСЯ между версиями (важно понимать заранее) ──
 *
 * 1. userId. v1 отдаёт число (1), v2 — UUID
 *    («019f73cc-e49f-7f60-bd32-5939977f2607»). Код, сравнивающий id,
 *    должен работать со строками: см. sameUser() в api/identity.js.
 *
 * 2. Регистрация. v1 собирает имя, дату рождения, пол и тройную капчу.
 *    v2 принимает только email + пароль + ник; полей даты и пола у него
 *    НЕТ вовсе. Следствие: пол не сохраняется, а он определял скин
 *    по умолчанию (defaultSkinForGender). При v2 новый игрок получит
 *    женский скин, пока не выберет свой в разделе «Аватар».
 *    Это осознанная деградация, а не недосмотр — альтернатива
 *    (спрашивать пол и никуда не отправлять) была бы обманом.
 *
 * 3. Капча. В v2 её нет. При v2 капча не запрашивается и форма
 *    регистрации не должна её показывать.
 *
 * 4. OAuth. В схеме v2 есть только Яндекс (Google отсутствует), НО и он
 *    на сервере не настроен: `/auth/yandex/start` отвечает «Яндекс-вход
 *    не настроен» (проверено на боевом API 31.07.2026).
 *    Поэтому при v2 обе кнопки скрываются, остаётся вход по паролю.
 *    Как только Яндекс настроят — достаточно вернуть `yandexOAuth: true`
 *    в CAPABILITIES ниже, код обмена кодом уже написан.
 */

import * as v1 from "./auth";
import * as v2 from "./identityV2";

/** Активная версия. Читается один раз при загрузке модуля. */
export const IDENTITY_VERSION =
  (process.env.REACT_APP_IDENTITY || "v1").toLowerCase() === "v2" ? "v2" : "v1";

export const isV2 = IDENTITY_VERSION === "v2";

/* Возможности версии — чтобы интерфейс не показывал то, чего нет.
   Проверять их флагом, а не `isV2` напрямую: когда в v2 появится Google,
   поменяется одна строка здесь, а не пять мест в компонентах. */
export const CAPABILITIES = {
  captcha: !isV2,          // тройная капча есть только в v1
  /* Google — НЕ провайдер входа, а информационная заглушка: вход через
     Google в РФ недоступен по законодательству. Кнопка показывается
     всегда и открывает объяснение, поэтому флага «доступен» у неё нет. */
  googleOAuth: false,
  /* Яндекс настроен и в v1, и в v2 (выкачено 31.07.2026: переменные
     IDENTITY_YANDEX_* + патч identity, принимающий ?frontend=).
     Проверено на боевом: start отдаёт authorize_url с redirect_uri
     под домен, который передал фронт. */
  yandexOAuth: true,
  passwordRecovery: !isV2, // в схеме v2 forgot-password не найден
  birthDateAndGender: !isV2,
  sessions: isV2,          // управление устройствами — только v2
};

/** Единый вид пользователя для AuthContext. */
function shapeV2(me) {
  return {
    token: v2.getAccessToken(),
    userId: me?.id || null,
    username: me?.nickname || me?.username || null,
    photo: me?.avatar_url || null,
    // Поля ниже интерфейс использует опционально
    level: me?.level ?? 0,
    frame: me?.frame || null,
    nameColor: me?.name_color || null,
    emailConfirmed: !!me?.email_confirmed,
    role: me?.role || null,
  };
}

/* ── Вход ───────────────────────────────────────────────────────── */

export async function login({ email, password }) {
  if (!isV2) return v1.login({ email, password });
  const me = await v2.login(email, password);
  return shapeV2(me);
}

/* ── Регистрация ────────────────────────────────────────────────── */

export async function signupAndLogin(params) {
  if (!isV2) return v1.signupAndLogin(params);

  /* v2 требует ник. Форма Рублокса собирает «имя» (firstName) — его
     и используем: для человека это одно и то же поле, переименовывать
     подпись ради внутреннего названия API незачем. */
  const nickname = params.nickname || params.firstName || "";
  const me = await v2.signup({
    email: params.email,
    password: params.password,
    nickname,
  });
  return shapeV2(me);
}

/* ── OAuth ──────────────────────────────────────────────────────── */

/** Адрес, у которого спрашивают authorize_url для входа через Яндекс.
    v1 и v2 отвечают одинаково — {authorize_url}. */
export function yandexStartPath(frontend) {
  const f = encodeURIComponent(frontend || window.location.origin);
  return isV2
    ? `${v2.IDENTITY_BASE}/auth/yandex/start?frontend=${f}`
    : `/api-user/api/v1/users/auth/yandex/start?frontend=${f}`;
}

export async function exchangeYandexCode({ code, state }) {
  if (!isV2) return v1.exchangeYandexCode({ code, state });
  const me = await v2.yandexExchange(code, state);
  return shapeV2(me);
}

export async function exchangeGoogleCode({ code, state }) {
  if (isV2) {
    // Сюда не должны попасть: при v2 кнопка Google скрыта. Но если
    // человек вернулся по старой ссылке из письма или закладки —
    // объясняем, а не падаем непонятной ошибкой.
    throw new Error(
      "Вход через Google временно недоступен. Войди через Яндекс или по паролю."
    );
  }
  return v1.exchangeGoogleCode({ code, state });
}

/* ── Сессия ─────────────────────────────────────────────────────── */

export async function restoreSessionAsync() {
  if (!isV2) return v1.restoreSessionAsync();
  // v2: личность всегда берём у /me — токен мог протухнуть, а в нём
  // всё равно нет ни имени, ни аватара.
  if (!v2.getAccessToken()) return null;
  try {
    const me = await v2.me();
    return shapeV2(me);
  } catch {
    return null;
  }
}

export function restoreSession() {
  if (!isV2) return v1.restoreSession();
  const token = v2.getAccessToken();
  if (!token) return null;
  // Синхронное восстановление: до ответа /me знаем только сам факт входа.
  // Имя и аватар подтянет restoreSessionAsync.
  return { token, userId: null, username: null, photo: null };
}

export function logout() {
  if (!isV2) return v1.logout();
  // Не ждём ответа: интерфейс должен разлогиниться мгновенно, даже если
  // сеть лежит. Токены модуль чистит сам в finally.
  v2.logout().catch(() => {});
  return undefined;
}

export function isAuthenticated() {
  return isV2 ? !!v2.getAccessToken() : v1.isAuthenticated();
}

/* ── Профиль ────────────────────────────────────────────────────── */

export async function getProfile(signal) {
  if (!isV2) return v1.getProfile(signal);
  const me = await v2.me(signal);
  /* Приводим к форме, которую ждёт кабинет от v1: он читает firstName,
     photo и level. Иначе пришлось бы править CabinetContext и десяток
     экранов — а они к переезду отношения не имеют. */
  return {
    ...me,
    firstName: me?.nickname || me?.username || null,
    photo: me?.avatar_url || null,
    level: me?.level ?? 0,
    active_frame: me?.frame || null,
    name_color: me?.name_color || null,
  };
}

/* ── Экономика ──────────────────────────────────────────────────────
   Кошелёк уже живёт в v2 (`api/v2.js`), а `api/auth.js` лишь добавляет
   к нему запасной путь через v1. При IDENTITY=v2 запасной путь больше
   не нужен: v1 отключается, и обращаться к нему бессмысленно. */

export function getDiamonds(signal) {
  return v1.getDiamonds(signal);
}

export function getRating(signal) {
  return v1.getRating(signal);
}

/* ── Капча (только v1) ──────────────────────────────────────────── */

export async function isCaptchaRequired() {
  if (isV2) return false;
  return v1.isCaptchaRequired();
}

export function getCaptchaChallengePair() {
  if (isV2) return Promise.resolve(null);
  return v1.getCaptchaChallengePair();
}
